import {
  listTimeTitleFunc,
  formatChartParamsDate,
} from '@op/components/ChartCommon/formatter';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import { getPositiveNegativeIconName } from '@op/pages/Performance/components/MentionsSentiment/chart';
import { echartsContent } from '@op/utils';
import { formatTimeXAxisText, getChartPosition } from '@op/utils/chart';
import { reviewsFormat, percentageFormat } from '@op/utils/value';
import React, { useMemo } from 'react';
import { useIntl } from 'umi';

export default function MentionsCharts({
  chartMap,
  filter: filterParams,
  seriesDataKeys,
  isFullscreen,
  height=168
}: any) {
  const { formatMessage } = useIntl();
  // 【Techland专区】舆情页游戏商店过滤星标评论
  const mentionTitle = filterParams?.studioid === "techland" ? "Starred Reviews" : "Mentions";
  const [options, noData] = useMemo(() => {
    const series: { name: string; type: string; data: any[]; }[] = [];
    let xAxis: string[] = [];
    let totalList: Array<number> = [];
    const minTotalList: Array<number> = [];
    seriesDataKeys.map(
      ({ text, key, type, color, valueKey, beginColor, endColor }: any) => {
        const data = chartMap[key];
        if (data) {
          const seriesObj: any = {
            name: text,
            type,
            barMaxWidth: 80,
            data: data.map((item) => item[valueKey]),
            itemStyle: {
              color: color || {
                type: 'linear',
                x: 0,
                y: 1,
                x2: 1,
                y2: 0,
                colorStops: [
                  {
                    offset: 0,
                    color: beginColor, // 0% 处的颜色
                  },
                  {
                    offset: 1,
                    color: endColor, // 100% 处的颜色
                  },
                ],
                global: false, // 缺省为 false
              },
            },
          };
          if (type === 'bar') {
            seriesObj.stack = 'total';
            totalList = data.map((item: number, index: number) => {
              const total = totalList[index] || 0;
              return total + Math.abs(item || 0);
            });
          } else {
            minTotalList.push(...seriesObj.data);
            seriesObj.yAxisIndex = 1;
          }
          series.push(seriesObj);

          if (xAxis.length === 0 && data) {
            xAxis = data.map((item) => item.time);
          }
        }
      },
    );
    const currentOptions = {
      tooltip: {
        position: (
          pos: [any, any],
          params: any,
          dom: any,
          rect: any,
          size: { viewSize: [any, any]; },
        ) =>
          getChartPosition(pos, params, dom, rect, size, 50, 15, 'bottom', 1.2),
        appendToBody: false,
        confine: false,
        formatter: (params: any) => {
          const title = listTimeTitleFunc(
            params?.[0]?.axisValue,
            filterParams.date_type,
            true,
          );
          return formatChartParamsDate({
            params,
            title,
            listTitleFunc: (params) => {
              const name = params.seriesName;
              return getPositiveNegativeIconName(name, formatMessage);
            },
            isFilterZero: true,
            valueFormat: (value, ele) => {
              if (ele.seriesType === 'bar') {
                return reviewsFormat(Math.abs(value));
              }

              return percentageFormat(value * 100, '-', 0);
            },
            dealwithRecords(records) {
              const valueList = records.filter((item) => !(
                item.value.includes('%') || isNaN(Number(item.originValue))
              ));

              const total = valueList.reduce(
                (pre, cur) => pre + (cur?.originValue || 0),
                0,
              );
              records.unshift({
                text: formatMessage({ id: mentionTitle }),
                value: reviewsFormat(total),
                originValue: total,
                originText: 'Mentions',
              });
            },
          });
        },
      },
      axisPointer: getAxisPointer((data) =>
        formatTimeXAxisText(data.value, filterParams?.date_type),
      ),
      grid: {
        top: 10,
        right: 0,
        bottom: 20,
        containLabel: true,
      },
      legend: {
        show: false,
      },
      xAxis: {
        boundaryGap: ['20%', 0],
        data: xAxis,
        axisLabel: {
          showMaxLabel: true,
          fontSize: 8,
          color: 'rgba(38, 40, 50, 0.6)',
          formatter: (value: string) =>
            formatTimeXAxisText(value, filterParams?.date_type),
        },
      },
      yAxis: [
        {
          type: 'value',
          splitNumber: 2,
          axisLabel: {
            fontSize: 8,
            color: 'rgba(38, 40, 50, 0.6)',
            formatter: (value: any) => echartsContent(value),
          },
        },
        {
          type: 'value',
          splitNumber: 2,
          axisLabel: {
            fontSize: 8,
            color: 'rgba(38, 40, 50, 0.6)',
            // eslint-disable-next-line arrow-body-style
            formatter: (value: any) => {
              return `${Math.round(value * 100)}%`;
            },
          },
        },
      ],
      series,
    };
    return [currentOptions, series.length === 0];
  }, [chartMap, formatMessage, filterParams, mentionTitle]);

  return (
    <EChartCommon
      key={isFullscreen}
      optionsProps={{ type: 'bar', excludeOptions: ['axisPointer']  }}
      className="dbh-gamestore-mentions-chart dbh-gamestore-chart-legend"
      height={height}
      leftTitle={
        <div className="dbh-gamestore-chart-legend-left">
          {formatMessage({ id: mentionTitle })}
        </div>
      }
      rightTitle={
        <div className="dbh-gamestore-chart-legend-right">
          {formatMessage({ id: '30-Day Positive %' })}
        </div>
      }
      empty={noData}
      options={options}
      hasEmptyImg={false}
    ></EChartCommon>
  );
}
